/**
 * An inclusive range of local calendar dates with optional endpoints.
 */
export interface DateRange {
    from?: Date | null
    to?: Date | null
}

/**
 * A named shortcut evaluated relative to today.
 */
export interface DateRangePreset {
    label: string
    getRange: (today: Date) => DateRange
}

/**
 * Return a new Date at local midnight.
 */
export const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Add calendar days without relying on fixed millisecond durations.
 */
export const addDays = (date: Date, days: number): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

/**
 * Shift calendar months and clamp dates at the end of the target month.
 */
export const addMonths = (date: Date, months: number): Date => {
    const first = new Date(date.getFullYear(), date.getMonth() + months, 1)
    const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
    return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), lastDay))
}

/**
 * Copy supplied endpoints at local midnight, treating empty ranges as null.
 */
export const copyRange = (range: DateRange | null): DateRange | null => {
    if (!range || (!range.from && !range.to)) return null
    return {
        ...range,
        ...(range.from ? { from: startOfDay(range.from) } : {}),
        ...(range.to ? { to: startOfDay(range.to) } : {})
    }
}

/**
 * Check ordering, validity, and inclusive bounds.
 */
export const isRangeAllowed = (range: DateRange | null, minDate?: Date, maxDate?: Date): boolean =>
    !range || (
        [range.from, range.to].every(date => !date || (
            Number.isFinite(date.getTime()) &&
            (!minDate || date >= startOfDay(minDate)) &&
            (!maxDate || date <= startOfDay(maxDate))
        )) &&
        (!range.from || !range.to || range.from <= range.to)
    )

/**
 * The default rolling date range shortcuts.
 */
export const defaultPresets: DateRangePreset[] = [
    { label: 'Today', getRange: today => ({ from: startOfDay(today), to: startOfDay(today) }) },
    ...[3, 7, 30].map(days => ({
        label: `Last ${days} Days`,
        getRange: (today: Date) => ({ from: addDays(today, 1 - days), to: startOfDay(today) })
    })),
    ...[3, 6, 12].map(months => ({
        label: months === 12 ? 'Last 1 Year' : `Last ${months} Months`,
        getRange: (today: Date) => ({ from: addMonths(today, -months), to: startOfDay(today) })
    }))
]
